import React from 'react';
import { useIntl } from 'react-intl';
import { VIDEO_CREDIT_COST } from 'fym-shared/src/music24/music24Plans';
import { TypedMemo } from '../../../common/types';
import { useDirectedVideoEntry } from '../../../directedVideo/entry/useDirectedVideoEntry';
import { directedMessages } from '../../../directedVideo/messages/directedMessages';
import { entryMessages } from '../../../directedVideo/messages/entryMessages';
import { AssetPreviewTheme, AssetTypePreview } from './AssetTypePreview';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from './Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface DirectedVideoStudioCardProps {
  accountId: number;
  catalogItemUuid: string;
  hasMaster: boolean;
  masterReady: boolean;
  hasLyrics: boolean;
  previewTheme: AssetPreviewTheme;
}

/**
 * Studio → Full video → "Directed video · Beta" (design directed-video a4): the card after the music
 * video, with the look of the other full-video cards. It is there only with `canRenderFullSongVideos`
 * and a song with its audio and its lyrics. It opens the open directed video of the song, or makes
 * a new one.
 */
const DirectedVideoStudioCardComponent: React.FC<DirectedVideoStudioCardProps> = (props) => {
  const { accountId, catalogItemUuid, hasMaster, masterReady, hasLyrics, previewTheme } = props;
  const intl = useIntl();
  const entry = useDirectedVideoEntry({ accountId, catalogItemUuid, song: { hasMaster, masterReady, hasLyrics } });
  if (!entry.visible) {
    return null;
  }
  return (
    <button type="button" className={styles.card} onClick={entry.start} data-testid="studio-directed-video">
      <AssetTypePreview assetTypeId="musicVideo" theme={previewTheme} />
      <span className={styles.cardTop}>
        <span className={styles.credit}>{VIDEO_CREDIT_COST.aiClip}</span>
        <span className={`${styles.chip} ${styles.chipRun}`}>{intl.formatMessage(directedMessages.beta)}</span>
      </span>
      <span className={styles.cardName}>{intl.formatMessage(entryMessages.button)}</span>
      <span className={styles.cardLine}>{intl.formatMessage(entryMessages.studioLine)}</span>
      <span className={styles.cardMeta}>
        {intl.formatMessage(entryMessages.studioPrice, { credits: VIDEO_CREDIT_COST.aiClip })}
      </span>
    </button>
  );
};

export const DirectedVideoStudioCard = TypedMemo(DirectedVideoStudioCardComponent);
